import React, { Component } from "react";
import gql from "graphql-tag";
import { withApollo } from "react-apollo";
import { withRouter } from "react-router-dom";
import * as compose from "lodash.flowright";
import SelectDropDown from "../selectdropdown/SelectDropDown";
import ProfileDateBlock from "./ProfileDateBlock";
import { PageShimmer } from "./Shimmer";
import CreateDoubtForm from "./CreateDoubtForm";
import DoubtThreadDrawer from "./DoubtThreadDrawer";

// College bridge: the viewed student comes from props (route state). Different
// entry points name the mobile field differently, so check every known variant.
// The student-app tab keys on mobile.
const resolveMobile = (student) => {
  if (!student) return null;
  return (
    student.contact_no || student.student_mobile || student.mobile ||
    student.studentMobile || student.parent_mobile || student.phone ||
    student.mobile_no || student.username || null
  );
};

// College view: this profile shows ANOTHER student, so "You/Your" becomes the
// student's (first) name. Falls back to "Student" if no name.
const studentName = (student) => {
  const full = (student && (student.student_name || student.name)) || "";
  return full.trim() ? full.trim() : "Student";
};
const studentFirstName = (student) => studentName(student).split(/\s+/)[0];

const PAGE_SIZE = 10;

// Server-side paginated + searchable + subject/date-filtered doubt history.
const GET_STUDENT_DOUBT_HISTORY = gql`
  query getStudentDoubtHistory(
    $mobile: String, $student_user_id: Int, $edu_student_id: Int,
    $page: Int, $limit: Int, $search: String, $status: String,
    $subject_id: Int, $from_date: String, $to_date: String
  ) {
    getStudentDoubtHistory(
      mobile: $mobile, student_user_id: $student_user_id, edu_student_id: $edu_student_id,
      page: $page, limit: $limit, search: $search, status: $status,
      subject_id: $subject_id, from_date: $from_date, to_date: $to_date
    ) {
      raised
      resolved
      pending
      avg_tat_hours
      total
      subjects { id name }
      history {
        id
        status
        doubt
        tat
        tat_hours
        subject
      }
    }
  }
`;

const num = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

const isResolved = (status) => String(status || "").toLowerCase() === "resolved";

const tatLabel = (row) => {
  if (!isResolved(row.status)) return null;
  if (row.tat) return row.tat;
  if (row.tat_hours) return `${row.tat_hours}h`;
  return null;
};

class DoubtsTab extends Component {
  state = {
    // data
    data: { raised: 0, resolved: 0, pending: 0, avg_tat_hours: 0, total: 0, subjects: [], history: [] },
    subjects: [], // preserved across refetches so the dropdown never empties out
    loading: true,
    // controls (server-side)
    search: "",       // live input value
    filter: "all",    // all | resolved | pending
    subjectId: 0,     // 0 = all subjects
    dateStart: null,  // moment | null (range picker)
    dateEnd: null,    // moment | null
    page: 1,
    // ui
    showCreateDrawer: false,
    activeDoubt: null, // the doubt whose thread drawer is open (or null)
  };

  // Resolve the picked date range to unix-second bounds for the server.
  getDateBounds = () => {
    const { dateStart, dateEnd } = this.state;
    return {
      from_date: dateStart ? Math.floor(dateStart.clone().startOf("day").valueOf() / 1000) : null,
      to_date: dateEnd ? Math.floor(dateEnd.clone().endOf("day").valueOf() / 1000) : null,
    };
  };

  componentDidMount() {
    this.fetchDoubts();
  }

  componentWillUnmount() {
    if (this._searchTimer) clearTimeout(this._searchTimer);
  }

  // Refetch when the header exam-stream changes (kept consistent with other tabs).
  componentDidUpdate(prevProps) {
    if (prevProps.examStream !== this.props.examStream) {
      this.setState({ page: 1 }, this.fetchDoubts);
    }
  }

  fetchDoubts = async () => {
    this.setState({ loading: true });
    try {
      const { page, search, filter, subjectId } = this.state;
      const { from_date, to_date } = this.getDateBounds();
      const res = await this.props.client.query({
        query: GET_STUDENT_DOUBT_HISTORY,
        variables: {
          mobile: `${resolveMobile(this.props.student) || ""}`,
          // Backstop: college identity so the backend resolves the student even
          // when the passed student object has no mobile field.
          student_user_id: parseInt(this.props.student?.student_user_id) || 0,
          edu_student_id: parseInt(this.props.student?.edu_student_id) || 0,
          page,
          limit: PAGE_SIZE,
          search: search.trim() || null,
          status: filter === "all" ? null : filter,
          subject_id: subjectId || null,
          from_date: from_date != null ? String(from_date) : null,
          to_date: to_date != null ? String(to_date) : null,
        },
        fetchPolicy: "network-only",
      });
      const data = res?.data?.getStudentDoubtHistory || this.state.data;
      this.setState((prev) => ({
        data,
        // Keep the subject list once we have it (subsequent filtered fetches still
        // return the full list, but guard against an empty result blanking it).
        subjects: data.subjects && data.subjects.length ? data.subjects : prev.subjects,
        loading: false,
      }));
    } catch (e) {
      console.log(e);
      this.setState({ loading: false });
    }
  };

  // Subject dropdown change.
  onSubjectChange = (subjectId) => {
    this.setState({ subjectId: Number(subjectId) || 0, page: 1 }, this.fetchDoubts);
  };

  // Date-range picker applied (moment start/end).
  onDateRange = (start, end) => {
    this.setState({ dateStart: start, dateEnd: end, page: 1 }, this.fetchDoubts);
  };

  clearDateRange = () => {
    this.setState({ dateStart: null, dateEnd: null, page: 1 }, this.fetchDoubts);
  };

  // Debounced server-side search — waits for typing to settle, resets to page 1.
  onSearchChange = (value) => {
    this.setState({ search: value });
    if (this._searchTimer) clearTimeout(this._searchTimer);
    this._searchTimer = setTimeout(() => {
      this.setState({ page: 1 }, this.fetchDoubts);
    }, 400);
  };

  setFilter = (filter) => {
    if (filter === this.state.filter) return;
    this.setState({ filter, page: 1 }, this.fetchDoubts);
  };

  goToPage = (page) => {
    if (page < 1) return;
    this.setState({ page }, this.fetchDoubts);
  };

  // Refetch once a new doubt is raised, then close the drawer + reset controls.
  handleDoubtCreated = () => {
    this.setState(
      { showCreateDrawer: false, filter: "all", search: "", subjectId: 0, dateStart: null, dateEnd: null, page: 1 },
      this.fetchDoubts
    );
  };

  // Open a doubt's discussion thread inline in a side drawer (no navigation).
  openDoubt = (row) => {
    if (row && row.id != null) this.setState({ activeDoubt: row });
  };

  closeThread = () => this.setState({ activeDoubt: null });

  // "Ask a Doubt" action rendered in the TabSection header (right side).
  renderAskDoubtButton = () => (
    <button
      type="button"
      className="sp-ask-doubt-btn"
      onClick={() => this.setState({ showCreateDrawer: true })}
    >
      <i className="fas fa-plus mr-2" /> Ask a Doubt
    </button>
  );

  // Server-side search (debounced) + status chips + subject + date filters.
  renderControls = (counts) => {
    const { search, filter, subjectId, dateStart, dateEnd, subjects } = this.state;
    const chips = [
      { key: "all", label: `All (${counts.all})` },
      { key: "resolved", label: `Resolved (${counts.resolved})` },
      { key: "pending", label: `Pending (${counts.pending})` },
    ];
    const subjectOptions = [{ value: 0, label: "All subjects" }, ...subjects.map((s) => ({ value: s.id, label: s.name }))];
    const selectedSubject = subjectOptions.find((o) => o.value === subjectId) || subjectOptions[0];
    const dateActive = !!(dateStart || dateEnd);
    return (
      <>
        {/* Row 1 — search + status chips */}
        <div className="d-flex flex-wrap align-items-center mb-2" style={{ gap: 10 }}>
          <div className="sp-search-box">
            <i className="fas fa-search" />
            <input
              type="text"
              placeholder="Search doubts or subject…"
              value={search}
              onChange={(e) => this.onSearchChange(e.target.value)}
            />
            {search ? (
              <i className="fas fa-times" style={{ cursor: "pointer" }} title="Clear" onClick={() => this.onSearchChange("")} />
            ) : null}
          </div>
          {chips.map((c) => (
            <button key={c.key} type="button" className={`sp-filter-chip ${filter === c.key ? "active" : ""}`} onClick={() => this.setFilter(c.key)}>
              {c.label}
            </button>
          ))}
        </div>

        {/* Row 2 — subject dropdown + date-range picker */}
        <div className="d-flex flex-wrap align-items-center mb-3" style={{ gap: 10 }}>
          <div style={{ minWidth: 180 }}>
            <SelectDropDown
              stateData={selectedSubject}
              options={subjectOptions}
              name="subject"
              placeholderName="Subject"
              handleChange={(_n, value) => this.onSubjectChange(value)}
            />
          </div>

          <ProfileDateBlock
            start={dateStart || undefined}
            end={dateEnd || undefined}
            applyCallback={this.onDateRange}
            leftMode={false}
          />

          {(subjectId || dateActive) ? (
            <button
              type="button"
              className="sp-filter-chip"
              onClick={() => this.setState({ subjectId: 0, dateStart: null, dateEnd: null, page: 1 }, this.fetchDoubts)}
              title="Clear subject & date filters"
            >
              <i className="fas fa-times mr-1" style={{ fontSize: 10 }} />Clear
            </button>
          ) : null}
        </div>
      </>
    );
  };

  // Doubts as a compact table; clicking a row opens the thread drawer.
  renderDoubtsTable = (rows) => {
    const activeId = this.state.activeDoubt ? this.state.activeDoubt.id : null;
    return (
      <div className="sp-table-wrap">
        <table className="sp-doubts-table">
          <thead>
            <tr>
              <th>Subject</th>
              <th>Doubt</th>
              <th style={{ width: 120 }}>Status</th>
              <th style={{ width: 110 }}>Turnaround</th>
              <th style={{ width: 90 }} className="text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const resolved = isResolved(row.status);
              const tat = tatLabel(row);
              return (
                <tr
                  key={row.id ?? i}
                  className={activeId === row.id ? "is-active" : ""}
                  onClick={() => this.openDoubt(row)}
                  title="View discussion"
                >
                  <td>
                    <span className="sp-chip"><i className="fas fa-book" style={{ fontSize: 10 }} />{row.subject || "General"}</span>
                  </td>
                  <td className="sp-doubt-text" title={row.doubt || ""}>{row.doubt || "—"}</td>
                  <td>
                    <span className={`sp-status-pill ${resolved ? "is-resolved" : "is-pending"}`}>
                      <i className={`fas ${resolved ? "fa-check" : "fa-hourglass-half"}`} style={{ fontSize: 10 }} />
                      {resolved ? "Resolved" : "Pending"}
                    </span>
                  </td>
                  <td>
                    {tat ? (
                      <span className="text-dark font-weight-bold" style={{ fontSize: 12.5 }}>
                        <i className="far fa-clock mr-1 text-muted" />{tat}
                      </span>
                    ) : (
                      <span style={{ fontSize: 11.5, color: "#b45309" }}>Awaiting</span>
                    )}
                  </td>
                  <td className="text-right">
                    <span className="sp-row-open">Open <i className="fas fa-chevron-right" style={{ fontSize: 9 }} /></span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  renderFlaggedNote = (pending) => {
    if (!pending) return null;
    return (
      <div className="sp-animate d-flex align-items-center mt-2 mb-3 p-3" style={{ gap: 10, borderRadius: 12, background: "#fffbeb", border: "1px solid #fde68a" }}>
        <i className="fas fa-hourglass-half" style={{ color: "#b45309", fontSize: 15 }} />
        <span style={{ fontSize: 13, color: "#92400e" }}>
          <strong>{pending} pending doubt(s) beyond TAT</strong> — flagged to faculty for resolution
        </span>
      </div>
    );
  };

  // Server-side pagination controls: shows the current window + Prev/Next.
  renderPagination = (total) => {
    const { page, loading } = this.state;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    if (total <= PAGE_SIZE) return null;
    const from = (page - 1) * PAGE_SIZE + 1;
    const to = Math.min(page * PAGE_SIZE, total);
    const canPrev = page > 1 && !loading;
    const canNext = page < totalPages && !loading;
    return (
      <div className="d-flex align-items-center justify-content-between flex-wrap mt-3 pt-3" style={{ gap: 10, borderTop: "1px solid #f1f2f5" }}>
        <span className="text-muted" style={{ fontSize: 12.5 }}>
          Showing <strong className="text-dark">{from}&ndash;{to}</strong> of <strong className="text-dark">{total}</strong>
        </span>
        <div className="d-flex align-items-center" style={{ gap: 8 }}>
          <button className="sp-page-btn" disabled={!canPrev} onClick={() => this.goToPage(page - 1)}>
            <i className="fas fa-angle-left mr-1" />Prev
          </button>
          <span className="text-muted font-weight-bold" style={{ fontSize: 12.5 }}>Page {page} / {totalPages}</span>
          <button className="sp-page-btn" disabled={!canNext} onClick={() => this.goToPage(page + 1)}>
            Next<i className="fas fa-angle-right ml-1" />
          </button>
        </div>
      </div>
    );
  };

  render() {
    const { student } = this.props;
    const { data, loading, page, search, filter } = this.state;

    const raised = data.raised || 0;
    const resolved = data.resolved || 0;
    const pending = data.pending || 0;
    const total = data.total || 0;
    const history = data.history || [];
    const anyFilterActive = !!search || filter !== "all" || this.state.subjectId || this.state.dateStart || this.state.dateEnd;

    // Filter-chip counts come from the whole-set KPIs (server-side truth).
    const counts = { all: raised, resolved, pending };

    // First load (nothing fetched yet) shows the full shimmer; later refetches
    // (page/search/filter) show a lighter in-table state so controls stay live.
    const firstLoad = loading && total === 0 && !search && filter === "all" && page === 1;
    const hasAnyDoubts = raised > 0 || total > 0 || history.length > 0;

    return (
      <div className="sp-content-card" style={{ marginTop: 0 }}>
        {firstLoad ? (
          <PageShimmer />
        ) : !hasAnyDoubts && !loading ? (
          <div className="text-center text-muted py-5">
            <i className="far fa-comments d-block mb-2" style={{ fontSize: 34, color: "#cbd5e1" }} />
            <div style={{ fontSize: 14 }}>No doubts found for {student?.student_name || "this student"}.</div>
          </div>
        ) : (
          <>
            {/* Single card: heading + Ask a Doubt, then filters + table */}
            <div className="d-flex align-items-center justify-content-between flex-wrap mb-3" style={{ gap: 10 }}>
              <h6 className="font-weight-bold text-dark mb-0" style={{ fontFamily: "'Outfit', sans-serif" }}>Doubt Resolution History</h6>
              {this.renderAskDoubtButton()}
            </div>
            {this.renderControls(counts)}
            {this.renderFlaggedNote(pending)}

            <div style={{ position: "relative", minHeight: 120 }}>
                {loading ? (
                  <div className="d-flex align-items-center justify-content-center" style={{ position: "absolute", inset: 0, background: "rgba(255,255,255,0.65)", zIndex: 2, borderRadius: 10 }}>
                    <span className="text-muted" style={{ fontSize: 13 }}><i className="fas fa-circle-notch fa-spin mr-2" />Loading…</span>
                  </div>
                ) : null}

                {history.length > 0 ? (
                  <>
                    {this.renderDoubtsTable(history)}
                    {this.renderPagination(total)}
                  </>
                ) : (
                  <div className="text-center text-muted py-4" style={{ fontSize: 14 }}>
                    {anyFilterActive ? "No doubts match your filters." : "No doubts yet."}
                  </div>
                )}
            </div>
          </>
        )}

        {/* Create Doubt slide-in drawer (mirrors the Doubts page) */}
        <div
          className={`sp-drawer-overlay ${this.state.showCreateDrawer ? "open" : ""}`}
          onClick={() => this.setState({ showCreateDrawer: false })}
        />
        <div className={`sp-doubt-drawer ${this.state.showCreateDrawer ? "open" : ""}`}>
          <div className="sp-drawer-header">
            <h5 className="font-weight-bold text-dark mb-0">Raise a New Doubt</h5>
            <button
              type="button"
              className="sp-drawer-close"
              onClick={() => this.setState({ showCreateDrawer: false })}
            >
              &times;
            </button>
          </div>
          <div className="sp-drawer-body">
            {this.state.showCreateDrawer && (
              <CreateDoubtForm
                student={this.props.student}
                onDoubtCreated={this.handleDoubtCreated}
                onCancel={() => this.setState({ showCreateDrawer: false })}
              />
            )}
          </div>
        </div>

        {/* Doubt thread slide-in drawer (inline conversation, no navigation) */}
        <div
          className={`sp-drawer-overlay ${this.state.activeDoubt ? "open" : ""}`}
          onClick={this.closeThread}
        />
        <div className={`sp-doubt-drawer sp-thread-drawer ${this.state.activeDoubt ? "open" : ""}`}>
          {this.state.activeDoubt && (
            <>
              <div className="sp-drawer-header" style={{ alignItems: "flex-start" }}>
                <div style={{ minWidth: 0 }}>
                  <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
                    <span className="sp-chip"><i className="fas fa-book" style={{ fontSize: 10 }} />{this.state.activeDoubt.subject || "General"}</span>
                    <span className={`sp-status-pill ${isResolved(this.state.activeDoubt.status) ? "is-resolved" : "is-pending"}`}>
                      <i className={`fas ${isResolved(this.state.activeDoubt.status) ? "fa-check" : "fa-hourglass-half"}`} style={{ fontSize: 10 }} />
                      {isResolved(this.state.activeDoubt.status) ? "Resolved" : "Pending"}
                    </span>
                  </div>
                  <p className="text-dark font-weight-bold mb-0 mt-2" style={{ fontSize: 14, lineHeight: 1.4 }}>
                    {this.state.activeDoubt.doubt || "Doubt"}
                  </p>
                </div>
                <button type="button" className="sp-drawer-close ml-2" onClick={this.closeThread}>&times;</button>
              </div>
              <div className="sp-drawer-body p-0 d-flex flex-column" style={{ overflow: "hidden" }}>
                <DoubtThreadDrawer
                  student={this.props.student}
                  doubtId={this.state.activeDoubt.id}
                  onReplied={this.fetchDoubts}
                />
              </div>
            </>
          )}
        </div>
      </div>
    );
  }
}

export default compose(withApollo, withRouter)(DoubtsTab);
